import React from "react";
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  FormField,
  Button,
  Select,
  Grid,
  Alert,
  Textarea,
} from "@cloudscape-design/components";
import { RealmSelector } from "../Components";
import { ApiClient, KeyMasterService } from "../services";
import { KeyMasterAction } from "../services/KeyMasterService";
import { useKeyMaster } from "../hooks";
import { Stage, KEYMASTER_LABELS, KEYMASTER_DATA_TYPE_OPTIONS, KeyMasterDataType, NotifyFn } from "../constants";

interface KeyMasterPageProps {
  stage: Stage;
  onNotify: NotifyFn;
}

const service = new KeyMasterService(ApiClient.getInstance());

const ACTION_OPTIONS = [
  { value: "ENCRYPT", label: "ENCRYPT" },
  { value: "DECRYPT", label: "DECRYPT" },
] as const;

const GRID_3_COL = [{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }] as const;
const GRID_FORM = [{ colspan: 5 }, { colspan: 2 }, { colspan: 5 }] as const;

export const KeyMasterPage: React.FC<KeyMasterPageProps> = ({ stage, onNotify }) => {
  const {
    realm,
    setRealm,
    action,
    setAction,
    dataType,
    setDataType,
    inputValue,
    setInputValue,
    outputValue,
    loading,
    handleConvert,
    handleSwap,
    inputLabel,
    outputLabel,
    selectedAction,
  } = useKeyMaster({
    onNotify,
    convertFn: (value, dt, act, rlm) => service.convert(value, dt, act, rlm),
  });

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={KEYMASTER_LABELS.PAGE_DESCRIPTION}>
          {KEYMASTER_LABELS.PAGE_TITLE}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {stage === "prod" && (
          <Alert type="warning" header={KEYMASTER_LABELS.PROD_WARNING_HEADER}>
            {KEYMASTER_LABELS.PROD_WARNING_MESSAGE}
          </Alert>
        )}

        <Container>
          <SpaceBetween size="m">
            <Grid gridDefinition={[...GRID_3_COL]}>
              <FormField label={KEYMASTER_LABELS.REALM}>
                <RealmSelector value={realm} stage={stage} onChange={setRealm} />
              </FormField>
              <FormField label={KEYMASTER_LABELS.DATA_TYPE}>
                <Select
                  selectedOption={KEYMASTER_DATA_TYPE_OPTIONS.find((d) => d.value === dataType) || null}
                  options={[...KEYMASTER_DATA_TYPE_OPTIONS]}
                  onChange={(e) => setDataType(e.detail.selectedOption.value as KeyMasterDataType)}
                />
              </FormField>
              <FormField label={KEYMASTER_LABELS.ACTION}>
                <Select
                  selectedOption={selectedAction}
                  options={[...ACTION_OPTIONS]}
                  onChange={(e) => setAction(e.detail.selectedOption.value as KeyMasterAction)}
                />
              </FormField>
            </Grid>

            <Grid gridDefinition={[...GRID_FORM]}>
              <FormField label={inputLabel}>
                <Textarea value={inputValue} onChange={(e) => setInputValue(e.detail.value)} rows={4} />
              </FormField>
              <SpaceBetween size="s" direction="vertical" alignItems="center">
                <Button onClick={handleConvert} loading={loading} variant="primary">
                  {action}
                </Button>
                <Button onClick={handleSwap} iconName="refresh">
                  {KEYMASTER_LABELS.SWAP}
                </Button>
              </SpaceBetween>
              <FormField label={outputLabel}>
                <Textarea value={outputValue} readOnly rows={4} />
              </FormField>
            </Grid>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
};
